import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';
import { Gem, ArrowLeft, Check, Loader2, Info } from 'lucide-react';
import Navbar from '../components/Navbar';
import PaymentModal from '../components/PaymentModal';
import AccessModal from '../components/AccessModal';
import VipModal from '../components/VipModal';
import TokenBalanceCard from '../components/TokenBalanceCard';
import Button from '../components/ui/Button';
import { trackPurchase, trackInitiateCheckout, trackViewContent } from '../utils/metaPixel';

const Tokens = () => {
  const { user, updateUser, refreshUser, loading } = useAuth();
  const [message, setMessage] = useState('');
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [showAccessModal, setShowAccessModal] = useState(false);
  const [showVipModal, setShowVipModal] = useState(false);

  useEffect(() => {
    trackViewContent('tokens_page', 'page', { content_name: 'Tokens' });
  }, []);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-zinc-950">
        <div className="text-center">
          <Loader2 className="mx-auto mb-4 h-10 w-10 animate-spin text-rose-500" />
          <p className="text-sm text-zinc-400">Carregando...</p>
        </div>
      </div>
    );
  }

  const tokenPackages = [
    { id: 5, tokens: 50, price: 20.0, popular: false },
    { id: 6, tokens: 375, price: 50.0, popular: true },
    { id: 7, tokens: 500, price: 75.0, popular: false },
    { id: 4, tokens: 2000, price: 180.0, popular: false },
  ];

  const handlePurchase = (packageData) => {
    if (!user) {
      setShowAccessModal(true);
      return;
    }
    trackInitiateCheckout(packageData.price, 'BRL', [`tokens_${packageData.tokens}`]);
    setSelectedPackage(packageData);
    setShowPaymentModal(true);
  };

  const handlePaymentSuccess = () => {
    trackPurchase(selectedPackage.price, 'BRL', [`tokens_${selectedPackage.tokens}`]);
    updateUser({
      tokens: user.tokens + selectedPackage.tokens,
      hasPaid: true,
    });
    setMessage(`Pagamento aprovado! ${selectedPackage.tokens} tokens adicionados.`);
    setTimeout(() => setMessage(''), 5000);
    setShowPaymentModal(false);
    setSelectedPackage(null);
    refreshUser();
  };

  return (
    <div className="min-h-screen bg-zinc-950">
      <Navbar />

      <div className="mx-auto max-w-2xl px-4 pb-10 pt-20">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold text-white">Comprar tokens</h1>
            <p className="mt-1 text-sm text-zinc-400">
              Carregue créditos para gerar imagens com IA
            </p>
          </div>
          <Link
            to="/app"
            className="inline-flex items-center gap-1.5 text-sm text-zinc-400 transition-colors hover:text-white"
          >
            <ArrowLeft className="h-4 w-4" /> Voltar
          </Link>
        </div>

        <TokenBalanceCard
          tokens={user?.tokens || 0}
          isVip={user?.isVip}
          onRefresh={async () => await refreshUser()}
          onVip={() => setShowVipModal(true)}
        />

        {message && (
          <div className="mb-6 rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 text-center text-sm text-emerald-300">
            {message}
          </div>
        )}

        <div className="rounded-2xl border border-white/10 bg-zinc-900 p-6">
          <h2 className="mb-5 text-center text-base font-semibold text-white">Pacotes disponíveis</h2>

          <div className="space-y-3">
            {tokenPackages.map((pkg) => (
              <div
                key={pkg.id}
                className={`relative flex items-center justify-between rounded-xl border p-4 transition-colors ${
                  pkg.popular
                    ? 'border-rose-500/50 bg-rose-500/5'
                    : 'border-white/10 bg-white/[0.02] hover:border-white/20'
                }`}
              >
                {pkg.popular && (
                  <span className="absolute -top-2.5 left-4 rounded-full bg-amber-400 px-2.5 py-0.5 text-[11px] font-bold text-zinc-900">
                    Mais popular
                  </span>
                )}

                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-rose-500/15">
                    <Gem className="h-5 w-5 text-rose-400" />
                  </div>
                  <div>
                    <p className="text-base font-semibold text-white">{pkg.tokens} tokens</p>
                    <p className="text-sm text-zinc-400">R$ {pkg.price.toFixed(2)}</p>
                  </div>
                </div>

                <Button onClick={() => handlePurchase(pkg)} size="sm">
                  Comprar
                </Button>
              </div>
            ))}
          </div>

          <div className="mt-6 rounded-xl border border-white/10 bg-white/[0.02] p-4">
            <p className="mb-2.5 flex items-center gap-2 text-sm font-medium text-white">
              <Info className="h-4 w-4 text-rose-400" /> Como funciona
            </p>
            <ul className="space-y-1.5">
              {[
                'Cada processamento consome 25 tokens',
                'Os tokens não expiram',
                'Pagamento seguro via PIX',
                'Crédito instantâneo após confirmação',
              ].map((item) => (
                <li key={item} className="flex items-center gap-2 text-sm text-zinc-400">
                  <Check className="h-3.5 w-3.5 shrink-0 text-emerald-400" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {showPaymentModal && selectedPackage && (
        <PaymentModal
          isOpen={showPaymentModal}
          onClose={() => {
            setShowPaymentModal(false);
            setSelectedPackage(null);
          }}
          packageData={selectedPackage}
          onSuccess={handlePaymentSuccess}
        />
      )}

      <AccessModal
        isOpen={showAccessModal}
        onClose={() => setShowAccessModal(false)}
        title="Acesso necessário"
        message="Você precisa fazer login para comprar tokens"
        redirectTo="/tokens"
      />

      <VipModal isOpen={showVipModal} onClose={() => setShowVipModal(false)} />
    </div>
  );
};

export default Tokens;
